import { Game, Company } from "@/types-games";
import { LicenseFeature } from "@/lib/utils/licenseConfig";
import { hasPageAccess } from "@/lib/utils/permissions";
import { PAGE_CONFIGS, PageId } from "@/lib/config/pageItemConfig";


export interface SidebarItem {
  id: string;
  label: string;
  path?: string; // Make path optional since logout doesn't need navigation
  icon: string;
  tooltip?: string;
  requiresGame?: boolean;
  requiresOwner?: boolean;
  requiredLicense?: LicenseFeature; // License feature required to access this item
  disabled?: boolean;
}

export interface SidebarGroup {
  id: string;
  label: string;
  items: SidebarItem[];
}

// Helper to create sidebar item from PageConfig
function createSidebarItem(
  pageId: PageId,
  requiredLicense?: LicenseFeature
): SidebarItem {
  const config = PAGE_CONFIGS[pageId];
  return {
    id: config.id,
    label: config.label,
    path: config.route,
    icon: config.icon,
    tooltip: config.tooltip,
    requiresGame: config.requiresGame,
    requiresOwner: config.requiresOwner,
    requiredLicense,
  };
}

// Build sidebar groups dynamically based on environment
const buildSidebarGroups = (): SidebarGroup[] => {
  const groups: SidebarGroup[] = [
    {
      id: "manage",
      label: "Manage",
      items: [
        createSidebarItem('home'),
      ],
    },
    {
      id: "main",
      label: "Structure",
      items: [
        // Use old IDs for backwards compatibility with permissions/visibility settings
        { ...createSidebarItem('templates', 'templates'), id: 'blueprints' },
        { ...createSidebarItem('library', 'library'), id: 'glossary' },
        createSidebarItem('dialogs', 'dialogs'),
        createSidebarItem('variables', 'variables'),
        // Logic page is only visible in dev mode
        ...(import.meta.env.DEV ? [createSidebarItem('logic', 'logic')] : []),
        createSidebarItem('documents', 'documents'),
      ],
    },
    {
      id: "text",
      label: "Text",
      items: [
        createSidebarItem('tms', 'tms'),
        createSidebarItem('images', 'images'),
        createSidebarItem('ui', 'ui'),
        createSidebarItem('relationship', 'relationships'),
      ],
    },
  ];

  // Add dev-only tools group in development mode
  if (import.meta.env.DEV) {
    groups.push({
      id: "dev-tools",
      label: "Dev Tools",
      items: [
        createSidebarItem('code-engine'),
      ],
    });
  }

  // Always add user settings at the end
  groups.push({
    id: "user-settings",
    label: "",
    items: [
      createSidebarItem('settings'),
      createSidebarItem('logout'),
    ],
  });

  return groups;
};

export const sidebarGroups: SidebarGroup[] = buildSidebarGroups();

export const getSidebarItems = (
  selectedGame: Game | null,
  selectedCompany: Company | null,
  isOwner: boolean = false,
  userEmail?: string,
  sidebarVisibility?: Array<{ groupId: string, visible: boolean, items: Array<{ id: string, visible: boolean }> }>
): SidebarGroup[] => {
  const companyLicense = selectedCompany?.license;
  
  return sidebarGroups
    .map((group) => {
      // Check if this group is visible in user settings
      const groupVisibility = sidebarVisibility?.find(g => g.groupId === group.id);
      const isGroupVisible = groupVisibility?.visible !== false; // Default to visible if not specified
      
      if (!isGroupVisible && group.id !== 'user-settings') {
        // User has hidden this group (except user-settings which can't be hidden)
        return null;
      }
      
      return {
        ...group,
        items: group.items
          .filter((item) => {
            // Filter out items that require owner role if user is not owner
            if (item.requiresOwner && !isOwner) {
              return false;
            }
            
            // Filter out items that require a license feature the company doesn't have
            if (item.requiredLicense && companyLicense) {
              const hasLicense = companyLicense[item.requiredLicense] === true;
              if (!hasLicense) {
                return false; // Hide items that aren't in the license
              }
            }
            
            // Filter out pages the user doesn't have access to (permissions check)
            if (selectedCompany?.id && item.id) {
              // Skip logout item (doesn't need page access check)
              if (item.id === 'logout') {
                return true;
              }
              
              const hasAccess = hasPageAccess(selectedCompany.id, item.id);
              if (!hasAccess) {
                return false;
              }
            }
            
            // Check if this specific item is visible in user settings
            const itemVisibility = groupVisibility?.items.find(i => i.id === item.id);
            const isItemVisible = itemVisibility?.visible !== false; // Default to visible if not specified
            
            // Settings and logout are always visible (can't be hidden)
            if (item.id === 'settings' || item.id === 'logout') {
              return true;
            }
            
            if (!isItemVisible) {
              return false; // User has hidden this item
            }
            
            return true;
          })
          .map((item) => {
            const isDisabled = item.requiresGame && !selectedGame;
            const tooltip = isDisabled
              ? "Select a project first"
              : item.tooltip || item.label;

            // Replace profile label with user email if available
            const label = item.id === "profile" && userEmail ? userEmail : item.label;

            return {
              ...item,
              label,
              tooltip,
              disabled: isDisabled,
            };
          }),
      };
    })
    .filter(group => group !== null && group.items.length > 0) as SidebarGroup[]; // Remove null groups and empty groups
};

export const getSidebarContent = (
  selectedGame: Game | null,
  selectedCompany: Company | null,
  isOwner: boolean = false,
  userEmail?: string,
  sidebarVisibility?: Array<{ groupId: string, visible: boolean, items: Array<{ id: string, visible: boolean }> }>
): SidebarGroup[] => {
  return getSidebarItems(selectedGame, selectedCompany, isOwner, userEmail, sidebarVisibility).filter(
    (group) => group.id !== "user-settings"
  );
};

export const getSidebarFooter = (
  selectedGame: Game | null,
  selectedCompany: Company | null,
  isOwner: boolean = false,
  userEmail?: string,
  sidebarVisibility?: Array<{ groupId: string, visible: boolean, items: Array<{ id: string, visible: boolean }> }>
): SidebarGroup[] => {
  return getSidebarItems(selectedGame, selectedCompany, isOwner, userEmail, sidebarVisibility).filter(
    (group) => group.id === "user-settings"
  );
}; 